/**
 * The seam between "pick a point on a map" and whichever map draws it.
 *
 * OpenStreetMap today, Google later — that swap was a stated requirement
 * before a line of this was written, so the map library is confined to one
 * file behind this interface, the same way a payment gateway is. Nothing above
 * this line imports Leaflet, and a Google implementation is a second file plus
 * one line in the registry.
 *
 * The interface is IMPERATIVE rather than a React component on purpose. Map
 * libraries own a DOM node and a lot of mutable state; wrapping that in a
 * component per vendor means re-deriving the same lifecycle bugs for each one.
 * A handle with `setCenter` and `destroy` is the smallest thing both vendors
 * can honestly implement.
 */

export interface LatLng {
  lat: number;
  lng: number;
}

export interface MapPickerOptions {
  /** Where to open. The pin starts here. */
  center: LatLng;
  zoom: number;
  /** Fires as the customer moves the map. The pin is fixed to the centre. */
  onCenterChanged: (center: LatLng) => void;
  /** Announced to assistive tech and used as the container's accessible name. */
  label: string;
}

export interface MapPickerHandle {
  /**
   * Move the map WITHOUT reporting it as the customer's answer.
   *
   * Only a move the customer made counts, so a programmatic recentre — onto
   * the PIN code's area, or a device fix — must not come back through
   * `onCenterChanged`. Whoever calls this knows what the move means and says
   * so itself.
   */
  setCenter: (center: LatLng, zoom?: number) => void;
  /** Release the DOM node and every listener. Must be safe to call twice. */
  destroy: () => void;
}

export interface MapProvider {
  readonly name: string;
  /**
   * Draw a picker into `container`.
   *
   * Async because a vendor's script and stylesheet may still be loading; the
   * caller shows its own placeholder until this resolves.
   */
  create: (container: HTMLElement, options: MapPickerOptions) => Promise<MapPickerHandle>;
}

/**
 * Where the map opens when we have nothing better.
 *
 * Kozhikode, because that is where the shop is: a customer who declines the
 * location prompt should start looking at their own town, not at the middle of
 * the country. Overridden the moment a device fix or a saved pin arrives.
 */
export const DEFAULT_CENTER: LatLng = { lat: 11.2588, lng: 75.7804 };

/** Close enough to see individual buildings, which is what a doorstep needs. */
export const DEFAULT_ZOOM = 16;
/** What we jump to once a real fix arrives. */
export const LOCATED_ZOOM = 18;

/** Six decimals is ~11cm. More is noise, and it bloats every payload. */
export function roundCoordinate(value: number): number {
  return Math.round(value * 1e6) / 1e6;
}
